import { useMemo } from 'react'

import type { FilterOption } from '../components/filter/Filter'

const matchesSearch = (label: string, term: string): boolean =>
  label.toLowerCase().includes(term.toLowerCase())

const filterTree = <V>(
  options: Array<FilterOption<V>>,
  term: string
): Array<FilterOption<V>> => {
  if (!term) return options
  const result: Array<FilterOption<V>> = []
  options.forEach((option) => {
    if (option.kind === 'toggle') {
      result.push(option)
      return
    }
    if (option.children && option.children.length > 0) {
      const matchedChildren = filterTree(option.children, term)
      if (matchesSearch(option.label, term) || matchedChildren.length > 0) {
        result.push({ ...option, children: matchedChildren })
      }
      return
    }
    if (matchesSearch(option.label, term)) {
      result.push(option)
    }
  })
  return result
}

const countLeafMatches = <V>(options: Array<FilterOption<V>>): number => {
  let count = 0
  options.forEach((option) => {
    if (option.kind === 'toggle') return
    if (option.children && option.children.length > 0) {
      count += countLeafMatches(option.children)
    } else {
      count += 1
    }
  })
  return count
}

interface IUseFilterOptionsArgs<V> {
  options: Array<FilterOption<V>>
  searchTerm: string
}

interface IUseFilterOptionsResult<V> {
  visibleOptions: Array<FilterOption<V>>
  hasResults: boolean
}

export const useFilterOptions = <V>({
  options,
  searchTerm,
}: IUseFilterOptionsArgs<V>): IUseFilterOptionsResult<V> => {
  const visibleOptions = useMemo(() => filterTree(options, searchTerm.trim()), [
    options,
    searchTerm,
  ])
  const hasResults = useMemo(() => countLeafMatches(visibleOptions) > 0, [
    visibleOptions,
  ])
  return { visibleOptions, hasResults }
}
